import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Table from "../../../../../core/common/dataTable/index";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import { Spin } from "antd";
import { DateRange } from "react-date-range";
import { format } from "date-fns";
import { exam } from "../../../../../core/data/json/exam";
import { all_routes } from "../../../../router/all_routes";
import { useUserPermissions } from "../../../../UserPermissionsContext";
import AudioPlayer from "../../audioPlayer";

type Permission = { id: number; name: string };
type Admin = {
  student_name: string;
  student_scs: string;
  agent_name: string;
  call_start_time_: string;
  call_end_time_: string;
  call_duration_hms: string;
  conversation_duration_hms: string;
  caller_status: string;
  destination_status: string;
  call_status: string;
  aws_call_recording_file: string;
};

const PrismAgentWiseCallLogs = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const idParam = searchParams.get("id");
  const id = idParam ? Number(idParam) : null;
  const initialStartDate = searchParams.get("startDate");
  const initialEndDate = searchParams.get("endDate");
  const data = exam;
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Month is zero-based, so we add 1
  const day = String(today.getDate()).padStart(2, "0");
  const formattedDate = `${month}-${day}-${year}`;
  const defaultValue = dayjs(formattedDate);
  const jwtToken = Cookies.get("authToken");
  const { userPermissions, webApi } = useUserPermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [admin, setAdmin] = useState<Admin[]>([]);
  const [from, setFrom] = useState();
  const [to, setTo] = useState<number>();
  const [totalCount, setTotalCount] = useState();
  const [toastMessage, setToastMessage] = useState("");
  const [showSuccessToast, setShowSuccessToast] = useState(false);
   const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem("prismAgentCallSummaryoFParticularAgentCurrentPage");
    return savedPage ? JSON.parse(savedPage) : 1;
  });
  useEffect(() => {
    localStorage.setItem("prismAgentCallSummaryoFParticularAgentCurrentPage", JSON.stringify(currentPage));
  }, [currentPage]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchText, setSearchText] = useState<string>("");
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement | null }>({});
  const [showCalendar1, setShowCalendar1] = useState(false);
  // const [startDate, setStartDate] = useState<string | null>(null);
  // const [endDate, setEndDate] = useState<string | null>(null);

  const [dateRange, setDateRange] = useState([
    {
      startDate: initialStartDate
        ? new Date(initialStartDate)
        : new Date(new Date().setHours(0, 0, 0, 0)),
      endDate: initialEndDate
        ? new Date(initialEndDate)
        : new Date(new Date().setHours(0, 0, 0, 0)),
      key: "selection",
    },
  ]);

  const startDate = format(dateRange[0].startDate, "yyyy-MM-dd");
  const endDate = format(dateRange[0].endDate, "yyyy-MM-dd");

  const handlePlayPause = (id: string) => {
    const currentAudio = audioRefs.current[id];

    if (currentAudio) {
      if (playingAudio === id) {
        currentAudio.pause();
        setPlayingAudio(null);
      } else {
        if (playingAudio !== null) {
          const previousAudio = audioRefs.current[playingAudio];
          if (previousAudio) {
            previousAudio.pause();
          }
        }
        currentAudio
          .play()
          .then(() => setPlayingAudio(id))
          .catch((error) => {
            console.error("Error playing audio:", error);
            setPlayingAudio(null);
          });
      }
    }
  };

  const handleFetchData = () => {
    if (id !== null && startDate && endDate) {
      fetchData(id, currentPage, pageSize, searchText, startDate, endDate); // Call fetchData with date range
      // Hide the date range picker after selecting dates
      setShowCalendar1(false);
    }
  };

  const checkPermission = (action: any, subject: any) => {
    return userPermissions.some(
      (ability) => ability.action === action && ability.subject === subject
    );
  };

  useEffect(() => {
    if (!checkPermission("agent_prism_call_summary", "Records")) {
      navigate(routes.crmLandingPage);
    } else if (typeof id === 'number' && !isNaN(id)) {
      fetchData(
        id,
        currentPage,
        pageSize,
        searchText,
        startDate || undefined,
        endDate || undefined
      );
    }
  }, [userPermissions]);

  useEffect(() => {
    if (typeof id === "number" && !isNaN(id)) {
      fetchData(
        id,
        currentPage,
        pageSize,
        searchText,
        startDate || undefined,
        endDate || undefined
      );
    }
  }, [id, currentPage, pageSize, searchText, startDate, endDate]);

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
  };

  const getModalContainer = () => {
    const modalElement = document.getElementById("modal-datepicker");
    return modalElement ? modalElement : document.body; // Fallback to document.body if modalElement is null
  };

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  const fetchGroupsData = async (
    id: number,
    page: number,
    pageSize: number,
    searchText: string,
    startDate = "",
    endDate = ""
  ) => {
    const response = await fetch(
      `${webApi}/call/agent_wise_school_lead_call_logs/${id}/?page=${page}&page_size=${pageSize}&search=${searchText}&from_date=${startDate}&to_date=${endDate}`,
      options
    );
    if (!response.ok) {
      if (response.status === 404) {
        return await response.json();
      }
      const errorData = await response.json();
      if (errorData.code === "token_not_valid") {
        navigate("/academic/sri-chaitanya/crm/landing-page");
      }
      throw new Error(errorData.detail || "Failed to fetch user data");
    }
    return await response.json();
  };

  const fetchData = async (
    id: number,
    page: number,
    pageSize: number,
    searchText: string,
    startDate = "",
    endDate = ""
  ) => {
    setIsLoading(true); // Start loading
    try {
      const [groupsData] = await Promise.all([
        fetchGroupsData(id, page, pageSize, searchText, startDate, endDate),
      ]);
      setTotalPages(groupsData.total_pages);
      setAdmin(groupsData.results);
      setTotalCount(groupsData.total_count);
      setFrom(groupsData.from);
      setTo(groupsData.to);
      setCurrentPage(groupsData.current_page_number);
      setTotalCount(groupsData.total_count);
      setToastMessage(groupsData.message || "submitted successfully!");
      setShowSuccessToast(true);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  useEffect(() => {
    console.log(admin);
  }, [admin])

  const handleTableChange = (pagination: any) => {
    const { current, pageSize } = pagination;
    setCurrentPage(current);
    setPageSize(pageSize);
    // fetchData(
    //   id,
    //   current,
    //   pageSize,
    //   searchText,
    //   startDate || undefined,
    //   endDate || undefined
    // ); // Fetch new data based on the updated pagination
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    // fetchData(
    //   id,
    //   currentPage,
    //   pageSize,
    //   value,
    //   startDate || undefined,
    //   endDate || undefined
    // );
  };

  const [loading, setLoading] = useState(false);

  const handleExportClick = async () => {

    window.location.href = `${webApi}/exports/export_agentwise_wizklub_call_logs/${id}?token=${jwtToken}&from_date=${startDate}&to_date=${endDate}handleExportClick`
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "school_lead_name",
    },

    {
      title: "Agent Name",
      dataIndex: "agent_name",
    },
    {
      title: "Call Start Time",
      dataIndex: "call_start_time_",
    },
    {
      title: "Call End Time",
      dataIndex: "call_end_time_",
    },
    {
      title: "Call Duration (HMS)",
      dataIndex: "call_duration_hms",
    },
    {
      title: "Conversation Duration (HMS)",
      dataIndex: "conversation_duration_hms",
    },
    {
      title: "Caller Status",
      dataIndex: "caller_status",
    },
    {
      title: "Destination Status",
      dataIndex: "destination_status",
    },
    {
      title: "Call Status",
      dataIndex: "call_status",
    },
    {
      title: "AWS Call Recording File",
      dataIndex: "aws_call_recording_file",
      render: (aws_call_recording_file: string | null) =>
        aws_call_recording_file ? (
          <AudioPlayer src={aws_call_recording_file} />
        ) : null,
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                Prism Agent wise call logs
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  {/* <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li> */}
                  <li className="breadcrumb-item">
                    <Link to={routes.prismAgentCallSummary}>Agent Call Summary</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Prism Agent wise call logs
                  </li>
                </ol>
              </nav>
            </div>
            {/* <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              {checkPermission("can_export_wizklub_agent_wise_call_logs", "calllog") && (
                <button className="btn btn-primary me-2" onClick={handleExportClick} disabled={loading}>
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    "Export"
                  )}
                </button>
              )}
            </div> */}
          </div>
          {/* /Page Header */}
          {/* Guardians List */}
          <div
            className="card "
            style={{
              width: "100%",
              boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
              borderRadius: "10px",
            }}
          >
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Prism Agent wise call logs List</h4>
              <div
                className="d-flex align-items-center flex-wrap"
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div
                  className="d-flex align-items-center"
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <div style={{ position: "relative" }}>
                    <div
                      onClick={() => setShowCalendar1(!showCalendar1)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 15px",
                        background: "white",
                        borderRadius: "20px",
                        boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "#34303D",
                        cursor: "pointer",
                        border: "1px solid #ddd",
                        minWidth: "200px",
                        justifyContent: "center",
                        // marginTop: "5px",
                        marginRight: "5px",
                        marginBottom: "7px",
                      }}
                    >
                      {format(dateRange[0].startDate, "dd MMM yy")} –{" "}
                      {format(dateRange[0].endDate, "dd MMM yy")}
                      <span style={{ marginLeft: "10px" }}>▼</span>
                    </div>

                    {/* Calendar Popup (Positioned Below) */}
                    {showCalendar1 && (
                      <div
                        style={{
                          position: "absolute",
                          zIndex: 2000,
                          background: "white",
                          borderRadius: "10px",
                          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                          padding: "5px",
                          left: "-130px" /* Move the element to the left */,
                        }}
                      >
                        <DateRange
                          ranges={dateRange}
                          onChange={(item) =>
                            setDateRange([
                              {
                                startDate:
                                  item.selection.startDate || new Date(),
                                endDate: item.selection.endDate || new Date(),
                                key: "selection",
                              },
                            ])
                          }
                          moveRangeOnFirstSelection={false}
                          rangeColors={["blue"]} // Dark color from your UI
                        />
                      </div>
                    )}
                  </div>

                  <button
                    className="btn btn-primary"
                    style={{
                      backgroundColor: "#28a745",
                      borderColor: "#28a745",
                      color: "#fff",
                      height: "38px",
                      padding: "6px 12px",
                      display: "flex",
                      alignItems: "center",
                      // marginTop: "0.5rem",
                      marginBottom: "7px",
                      borderRadius: "19px",
                    }}
                    onClick={handleFetchData}
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              {/* <div
                className="serch-global text-right"
                style={{ marginRight: "15px" }}
              >
                <input
                  type="search"
                  className="form-control form-control-sm mb-3 w-auto float-end"
                  value={searchText}
                  placeholder="Search"
                  onChange={(e) => handleSearch(e.target.value)}
                  aria-controls="DataTables_Table_0"
                ></input>
              </div> */}
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    height: "100vh",
                    paddingTop: "20px",
                  }}
                >
                  <Spin /> {/* Display loader while loading */}
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={admin}
                  Selection={false}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    locale: { items_per_page: "" },
                    nextIcon: <span>Next</span>,
                    prevIcon: <span>Prev</span>,
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    total: totalCount,
                    showTotal: (total: any, range: any[]) =>
                      `${range[0]}-${range[1]} of ${total}`,
                    pageSizeOptions: ["10", "20", "30"],
                  }}
                  onChange={handleTableChange} // Move this outside of pagination
                  onSearch={handleSearch}
                  searchText={searchText}
                />
              )}
            </div>
          </div>
          {/* /Guardians List */}
        </div>
      </div>
      <>
        {/* Delete Modal */}
        <div className="modal fade" id="delete-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form>
                <div className="modal-body text-center">
                  <span className="delete-icon">
                    <i className="ti ti-trash-x" />
                  </span>
                  <h4>Confirm Deletion</h4>
                  <p>
                    You want to delete all the marked items, this cant be undone
                    once you delete.
                  </p>
                  <div className="d-flex justify-content-center">
                    <Link
                      to="#"
                      className="btn btn-light me-3"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <Link
                      to="#"
                      data-bs-dismiss="modal"
                      className="btn btn-danger"
                    >
                      Yes, Delete
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Delete Modal */}
      </>
    </div>
  );
};

export default PrismAgentWiseCallLogs;
